/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import crudImg from 'assets/images/crud.png'

export const _5 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>5 - CRUD</h2>
    <img className={styles.img} src={crudImg} alt="crud" />
    <p className={styles.description}>
      <b>CRUD</b> — создание (create), чтение (read), обновление (update) и удаление (delete) - четыре основные метода
      для взаимодействия с ресурсами REST API.{' '}
    </p>
    <p className={styles.description}>В REST-архитектуре CRUD соответствует следующим HTTP-методам. </p>
    <ul className={styles.list}>
      <li className={styles.list__item}>
        <code className={styles.code}>POST (create)</code> — создать новый ресурс
      </li>
      <li className={styles.list__item}>
        <code className={styles.code}>GET (read)</code> — получить набор ресурсов или определенный ресурс по
        идентификатору
      </li>
      <li className={styles.list__item}>
        <code className={styles.code}>PUT (update)</code> или <code className={styles.code}>PATCH (update)</code> —
        обновить ресурс по идентификатору
      </li>
      <li className={styles.list__item}>
        <code className={styles.code}>DELETE (delete)</code> — удалить ресурс по идентификатору
      </li>
    </ul>
    <p className={styles.description}>
      Пусть API хранит массив постов, каждый из которых - это объект такого формата.{' '}
    </p>
    <CopyBlock
      text={`{
  id: <Integer>,
  author: <String>,
  content: <String>,
}
`}
      language="js"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      В примерах используем{' '}
      <a className={styles.link} href="https://jsonplaceholder.typicode.com/" target="_blank" rel="noopener noreferrer">
        JSONPlaceholder{' '}
      </a>
      , REST API для прототипирования.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>5.1 - Создание (create, POST)</h3>
      <p className={styles.description}>
        Используется, когда необходимо добавить новый ресурс (пост). Функция должна отправить на сервер запрос, в теле
        которого будет объект с полями <code className={styles.code}>author</code> и{' '}
        <code className={styles.code}>body</code>. RESTful API, по получению запроса, добавит объекту{' '}
        <code className={styles.code}>id</code> и в ответе вернет этот ресурс.{' '}
      </p>
      <CopyBlock
        text={`const postToAdd = {
  author: 'Mango',
  content: 'CRUD is awesome',
};

fetch('https://jsonplaceholder.typicode.com/posts', {
  method: 'POST',
  body: JSON.stringify(postToAdd),
  headers: {
    'Content-Type': 'application/json; charset=UTF-8',
  },
})
  .then(response => response.json())
  .then(post => console.log(post))
  .catch(error => console.log(error));`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        В ответ, если все хорошо, получим объект с добавленным <code className={styles.code}>id</code>. Идентификатор
        будет уникальным для каждого объекта.{' '}
      </p>
      <CopyBlock
        text={`{
  id: 1,
  author: 'Mango',
  content: 'CRUD is awesome',
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>5.2 - Чтение (read, GET)</h3>
      <p className={styles.description}>
        Используется, когда необходимо получить существующие данные. Функция должна отправить на сервер запрос без тела.
        Бекенд, по получению запроса, обработает его и в ответе вернет необходимые ресурсы.{' '}
      </p>
      <p className={styles.description}>Делаем запрос на все посты. </p>
      <CopyBlock
        text={`fetch('https://jsonplaceholder.typicode.com/posts')
  .then(response => response.json())
  .then(posts => console.log(posts))
  .catch(error => console.log(error));`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Просим один пост по <code className={styles.code}>id</code>, например пост с{' '}
        <code className={styles.code}>id=1</code>.
      </p>
      <CopyBlock
        text={`const postId = 1;

fetch(\`https://jsonplaceholder.typicode.com/posts/$ {postId}\`)
  .then(response => response.json())
  .then(post => console.log(post))
  .catch(error => console.log(error));`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>5.3 - Обновление (update, PUT и PATCH)</h3>
      <p className={styles.description}>
        Используется, когда необходимо обновить существующие данные. Функция должна отправить на сервер запрос, в теле
        которого необходимо указать объект с полями для изменения. Бекенд, по получению запроса, обработает его и в
        ответе вернет обновленный ресурс.
      </p>
      <CopyBlock
        text={`const postId = 1;
const postToUpdate = {
  content: 'CRUD is really awesome',
};

fetch(\`https://jsonplaceholder.typicode.com/posts/$ {postId}\`, {
  method: 'PATCH',
  body: JSON.stringify(postToUpdate),
  headers: {
    'Content-Type': 'application/json; charset=UTF-8',
  },
})
  .then(response => response.json())
  .then(post => console.log(post))
  .catch(error => console.log('ERROR' + error));`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>В ответ, если все хорошо, получим обновленный объект. </p>
      <CopyBlock
        text={`{
  id: 1,
  author: 'Mango',
  content: 'CRUD is really awesome',
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.notificationInfo}>
        Метод <code className={styles.code}>PATCH</code> заменяет в существующем ресурсе значения переданных в теле
        запроса свойств. Метод <code className={styles.code}>PUT</code> полностью заменяет ресурс.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>5.4 - Удаление (delete, DELETE)</h3>
      <p className={styles.description}>
        Используется, когда необходимо удалить существующие данные. Функция должна отправить на сервер запрос без тела.
        Бекенд, по получению запроса, обработает его, удалит ресурс из коллекции и в ответе вернет статус результата.{' '}
      </p>
      <CopyBlock
        text={`const postIdToDelete = 1;

fetch(\`https://jsonplaceholder.typicode.com/posts/$ {postIdToDelete}\`, {
  method: 'DELETE',
})
  .then(() => console.log('success'))
  .catch(error => console.log('ERROR' + error));`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
  </section>
))
